// src/pages/packages/PackageLandingPage.tsx
"use client"
import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate, useParams, } from "react-router-dom"
import { getPackageById, } from "../../api/packageService"
import { getCourseById, getCourseModulesByCourseId, getLessonsByModuleId } from "../../api/courseService"
import { ArrowLeft, CheckCircle, ChevronDown, Play, ImageIcon, File, HelpCircle } from "lucide-react"
import NotFoundPage from "../../components/common/NotFoundPage";
import { AUTH_TOKEN_KEY } from "../../utils/constants";
import { formatSeconds } from '../../utils/time';
// Helper function to append token to URLs (same as in courseService)
const appendTokenToUrl = (url: string | null): string | null => {
  if (!url) return null;
  
  // Skip if it's already a base64 image or already has a token
  if (url.startsWith('data:image') || url.includes('token=')) {
    return url;
  }
  
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) return url;
  
  // Check if URL already has query parameters
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
};

const PackageLandingPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [packageData, setPackageData] = useState<any>(null)
  const [courseDetails, setCourseDetails] = useState<any[]>([])
  const [selectedPriceOptionIndex, setSelectedPriceOptionIndex] = useState<number>(0)
  const [priceOptions, setPriceOptions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showFullDescription, setShowFullDescription] = useState(false)
  const [activeFaq, setActiveFaq] = useState<number | null>(null)
  const [coursesLoading, setCoursesLoading] = useState(false)
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set())
  const [coursesWithLessons, setCoursesWithLessons] = useState<any[]>([])
  
  // Get the selected price option object from the index
  const selectedPriceOption = priceOptions[selectedPriceOptionIndex] || null;
  
  // Function to format validity period
  const formatValidityPeriod = (period: string) => {
    switch (period) {
      case '1month':
        return '1 month'
      case '3months':
        return '3 months'
      case '6months':
        return '6 months'
      case '1year':
        return '1 year'
      default:
        if (period && period.includes('days')) {
          return period
        }
        return period || 'Lifetime'
    }
  }
  
  useEffect(() => {
    const fetchPackage = async () => {
      try {
        setLoading(true)
        const response = await getPackageById(id!)
        console.log("Package API Response:", response)
        if (response.status === 200 && response.data) {
          const data = response.data
          
          // Check if package is published - if not, show 404 for ALL users
          const isPublished = data.status === "Published"
          
          // ONLY show content if it's published, regardless of user type
          if (!isPublished) {
            setError("Package not found")
            return
          }
          
let bannerImage = ""

if (data.bannerBase64) {
  const rawBase64 = data.bannerBase64

  console.log("Raw banner base64:", rawBase64?.substring(0, 100))

  if (rawBase64.startsWith("data:image/")) {
    bannerImage = rawBase64
  } else {
    bannerImage = `data:image/jpeg;base64,${rawBase64}`
  }

  console.log(
    "Processed banner image:",
    bannerImage.substring(0, 100)
  )
} else if (data.bannerUrl) {
  bannerImage = data.bannerUrl
}          
          
setPackageData({
  id: data._id,
  title: data.title,
  description: data.description || "No description provided",

  bannerImage: bannerImage,
  bannerBase64: data.bannerBase64 || "",
  bannerUrl: data.bannerUrl || "",

  themeBase64: data.themeBase64 || "",
  themeUrl: data.themeUrl || "",

  price: data.price,
  priceDetails: data.priceDetails || [],
  faqs: data.faqs || [],
  courseIds: data.courseIds || [],
  status: data.status || "Available",
})          
          
          // Set price options from the priceDetails array
          if (data.priceDetails && Array.isArray(data.priceDetails)) {
            console.log("Setting price options:", data.priceDetails);
            setPriceOptions(data.priceDetails)
            setSelectedPriceOptionIndex(0)
          }
        } else {
          setError(response.message || "Failed to load package details")
        }
      } catch (err) {
        console.error("Error fetching package:", err)
        setError("Please check your authentication and try again")
      } finally {
        setLoading(false)
      }
    }
    
    if (id) {
      fetchPackage()
    }
  }, [id])
  
  // Fetch course details when package data is loaded
  useEffect(() => {
    const fetchCourseDetails = async () => {
      if (!packageData || !packageData.courseIds || packageData.courseIds.length === 0) {
        setCourseDetails([]);
        setCoursesLoading(false);
        return;
      }

      try {
        setCoursesLoading(true);
        console.log("Fetching course details for IDs:", packageData.courseIds);
        
        // Fetch details for each course
        const coursePromises = packageData.courseIds.map(async (courseId: string) => {
          try {
            const response = await getCourseById(courseId);
            console.log(`Course ${courseId} response:`, response);
            if (response.status === 200 && response.data) {
              return response.data;
            }
            console.error(`Failed to fetch course details for ID ${courseId}:`, response.message);
            return null;
          } catch (error) {
            console.error(`Error fetching course details for ID ${courseId}:`, error);
            return null;
          }
        });
        
        const coursesData = await Promise.all(coursePromises);
        // Filter out any null responses (failed requests)
        const validCourses = coursesData.filter(course => course !== null);
        
        console.log("Fetched course details:", validCourses);
        setCourseDetails(validCourses);
      } catch (error) {
        console.error("Error fetching course details:", error);
        setError("Failed to load course details");
      } finally {
        setCoursesLoading(false);
      }
    };

    if (packageData) {
      fetchCourseDetails();
    }
  }, [packageData]);
  
  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription)
  }
  
  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index)
  }
  
  const toggleCourse = async (courseId: string) => {
    setExpandedCourses(prev => {
      const newSet = new Set(prev)
      if (newSet.has(courseId)) {
        newSet.delete(courseId)
      } else {
        newSet.add(courseId)
        // Fetch lessons for this course when expanding
        fetchCourseLessons(courseId)
      }
      return newSet
    })
  }
  
  const fetchCourseLessons = async (courseId: string) => {
    try {
      // Check if we already have lessons for this course
      if (coursesWithLessons.some(course => course._id === courseId)) {
        return
      }
      
      // Fetch modules for the course
      const modulesResponse = await getCourseModulesByCourseId(courseId)
      if (modulesResponse.status === 200 && modulesResponse.data) {
        const modules = modulesResponse.data
        
        // Fetch lessons for each module
        const modulesWithLessons = await Promise.all(
          modules.map(async (module: any) => {
            try {
              const lessonsResponse = await getLessonsByModuleId(module._id)
              if (lessonsResponse.status === 200 && lessonsResponse.data) {
                return {
                  ...module,
                  lessons: lessonsResponse.data
                }
              } else {
                return {
                  ...module,
                  lessons: []
                }
              }
            } catch (error) {
              console.error(`Error fetching lessons for module ${module._id}:`, error)
              return {
                ...module,
                lessons: []
              }
            }
          })
        )
        
        // Update the courses with lessons
        setCoursesWithLessons(prev => {
          const updated = prev.filter(course => course._id !== courseId)
          const courseToUpdate = courseDetails.find(course => course._id === courseId)
          if (courseToUpdate) {
            updated.push({
              ...courseToUpdate,
              modules: modulesWithLessons
            })
          }
          return updated
        })
      }
    } catch (error) {
      console.error('Error fetching course lessons:', error)
    }
  }
  
  const truncateDescription = (text: string, maxWords = 30) => {
    if (!text) return ""
    const words = text.split(" ")
    if (words.length > maxWords) {
      return words.slice(0, maxWords).join(" ") + "..."
    }
    return text
  }
  
  // Get auth token for URLs
const authToken = localStorage.getItem(AUTH_TOKEN_KEY)

const bannerSrc = packageData?.bannerImage || "";
  
  // Function to handle buy now
  const handleBuyNow = () => {
    // Navigate to signup page
    navigate("/signup")
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-purple-500"></div>
      </div>
    )
  }
  
  if (error) {
    return (
      <NotFoundPage
        title="Package Coming Soon!"
        subtitle="Package Not Available"
        message="The package you're looking for is currently unavailable. It might be in development or preparing for launch. Please check back later or explore our other exciting packages!"
        buttonText="Back to Login"
        gradientFrom="from-purple-500"
        gradientTo="to-indigo-600"
        iconColor="bg-purple-500"
      />
    );
  }
  
  if (!packageData) {
    return (
      <NotFoundPage
        title="Package Coming Soon!"
        subtitle="Package Not Available"
        message="The package you're looking for is currently unavailable. It might be in development or preparing for launch. Please check back later or explore our other exciting packages!"
        buttonText="Back to Login"
        gradientFrom="from-purple-500"
        gradientTo="to-indigo-600"
        iconColor="bg-purple-500"
      />
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Enhanced Back Button */}
        <button
          onClick={() => navigate("/package")}
          className="flex items-center text-gray-600 hover:text-purple-600 mb-6 transition-all duration-200 group"
        >
          <ArrowLeft size={18} className="mr-2 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium text-sm">Back to packages</span>
        </button>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-5">
            {/* Package Title Header */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{packageData.title}</h1>
                  <p className="text-sm md:text-base text-gray-600 mb-3">Comprehensive Learning Package</p>
                </div>
                {/* Status badge - only shown for published content, no special permissions needed */}
                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                  packageData.status === "Published" ? "bg-green-50 text-green-700 border-green-100" :
                  "bg-gray-50 text-gray-700 border-gray-100"
                }`}>
                  <div className={`w-2 h-2 rounded-full mr-1 ${
                    packageData.status === "Published" ? "bg-green-500" :
                    "bg-gray-500"
                  }`}></div>
                  {packageData.status}
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                  <Play className="w-3 h-3 mr-1" />
                  {courseDetails.length || 0} Courses
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Lifetime Access
                </span>
              </div>
            </div>
            
            {/* About Package */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">About this package</h2>
              <div className="text-gray-700 leading-relaxed">
                {showFullDescription ? (
                  <p className="mb-3 text-sm">{packageData.description}</p>
                ) : (
                  <p className="mb-3 text-sm">{truncateDescription(packageData.description)}</p>
                )}
                <button
                  onClick={toggleDescription}
                  className="text-purple-600 hover:text-purple-800 font-medium text-xs transition-colors"
                >
                  {showFullDescription ? "Show Less" : "Show More"}
                </button>
              </div>
            </div>
            
            {/* Courses List */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Included Courses ({courseDetails.length})
              </h3>
              
              {coursesLoading ? (
                <div className="flex justify-center items-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-500"></div>
                </div>
              ) : courseDetails.length > 0 ? (
                <div className="space-y-3">
                  {courseDetails.map((course: any, index: number) => {
                    const isExpanded = expandedCourses.has(course._id)
                    const courseWithLessons = coursesWithLessons.find(c => c._id === course._id)
                    
                    // Process thumbnail to add token
                    let thumbnailSrc = "";
                    if (course.thumbnail) {
                      if (course.thumbnail.startsWith('http')) {
                        // It's a URL, append token
                        thumbnailSrc = appendTokenToUrl(course.thumbnail) || course.thumbnail;
                      } else if (course.thumbnail.startsWith('data:image')) {
                        // It's already a data URI
                        thumbnailSrc = course.thumbnail;
                      } else {
                        // It's likely a base64 string, convert to data URI
                        thumbnailSrc = `data:image/jpeg;base64,${course.thumbnail}`;
                      }
                    }
                    
                    return (
                      <div key={course._id} className="space-y-3">
                        <div 
                          className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={() => toggleCourse(course._id)}
                        >
                          {/* Course Thumbnail Preview */}
                          <div className="relative aspect-[16/9] w-16 h-10 mr-3 flex-shrink-0">
                            <div className="relative w-full h-full rounded-md overflow-hidden bg-gray-200 border border-gray-300">
                              {thumbnailSrc ? (
                                <img
                                  src={thumbnailSrc}
                                  alt={course.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    target.src = "https://via.placeholder.com/64x40/f3f4f6/6b7280?text=Course"
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-500">
                                  <div className="text-center">
                                    <ImageIcon size={14} className="mx-auto mb-1 text-gray-400" />
                                    <span className="text-xs font-medium">No Preview</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Course Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 pr-2">
                                <h4 className="font-semibold text-gray-900 text-sm mb-1 truncate">
                                  {course.title}
                                </h4>
                                <p className="text-xs text-gray-600 mb-1.5">{course.language}</p>
                                {course.description && (
                                  <p className="text-xs text-gray-500 line-clamp-2">{course.description}</p>
                                )}
                              </div>
                              
                              <div className="ml-2 flex-shrink-0">
                                <ChevronDown className={`text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} size={16} />
                              </div>
                            </div>
                            
                            {/* Course Info Tags */}
                            <div className="flex items-center gap-1.5 mt-1.5">
                              <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-full border border-purple-100">
                                <Play className="w-2.5 h-2.5 mr-1" />
                                Course Content
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Lessons for this course - only shown when expanded */}
                        {isExpanded && courseWithLessons?.modules && (
                          <div className="ml-3.5 pl-5 border-l-2 border-purple-200 space-y-3">
                            {courseWithLessons.modules.map((module: any, moduleIndex: number) => (
                              <div key={module._id} className="space-y-2.5">
                                <div className="flex items-center p-2.5 bg-purple-50 rounded-lg border border-purple-100">
                                  <div className="w-6 h-6 bg-purple-500 rounded-md mr-2.5 flex items-center justify-center">
                                    <Play className="text-white" size={12} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 text-xs truncate">{module.title}</p>
                                    <p className="text-xs text-gray-600 truncate">
                                      {module.description || "No description available"}
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Lessons for this module */}
                                {module.lessons && module.lessons.length > 0 && (
                                  <div className="ml-3.5 pl-5 border-l-2 border-gray-200 space-y-2.5">
                                    {module.lessons.map((lesson: any) => (
                                      <div key={lesson._id} className="flex items-start p-2.5 bg-white rounded-lg border border-gray-200">
                                        {/* Lesson type indicator */}
                                        <div className="flex-shrink-0 mr-2.5">
                                          {lesson.lessonType === 'video' ? (
                                            <div className="relative aspect-[16/9] w-14 h-10 rounded-md overflow-hidden">
                                              {lesson.lessonThumbnail ? (
                                                <img 
                                                  src={lesson.lessonThumbnail} 
                                                  alt={lesson.title} 
                                                  className="w-full h-full object-cover"
                                                  onError={(e) => {
                                                    const target = e.target as HTMLImageElement
                                                    target.src = "https://via.placeholder.com/56x38/f3f4f6/6b7280?text=Video"
                                                  }}
                                                />
                                              ) : (
                                                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                                  <Play className="text-gray-400" size={14} />
                                                </div>
                                              )}
                                              {/* Play icon overlay */}
                                              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                                <Play className="text-white" size={14} />
                                              </div>
                                            </div>
                                          ) : lesson.lessonType === 'document' ? (
                                            <div className="w-14 h-10 rounded-md bg-blue-50 flex items-center justify-center">
                                              <File className="text-blue-600" size={16} />
                                            </div>
                                          ) : lesson.lessonType === 'quiz' ? (
                                            <div className="w-14 h-10 rounded-md bg-purple-50 flex items-center justify-center">
                                              <HelpCircle className="text-purple-600" size={16} />
                                            </div>
                                          ) : (
                                            <div className="w-14 h-10 rounded-md bg-gray-100 flex items-center justify-center">
                                              <Play className="text-gray-400" size={16} />
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <h4 className="font-medium text-gray-900 text-xs truncate">{lesson.title}</h4>
                                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                            {lesson.description || "No description available"}
                                          </p>
                                          {/* Lesson type indicator with icon */}
                                          <div className="mt-1">
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-100">
                                              {lesson.lessonType === 'video' ? (
                                                <>
                                                  <Play className="w-2.5 h-2.5 mr-1" /> Video
                                                </>
                                              ) : lesson.lessonType === 'document' ? (
                                                <>
                                                  <File className="w-2.5 h-2.5 mr-1" /> Document
                                                </>
                                              ) : lesson.lessonType === 'quiz' ? (
                                                <>
                                                  <HelpCircle className="w-2.5 h-2.5 mr-1" /> Quiz
                                                </>
                                              ) : (
                                                <>
                                                  <Play className="w-2.5 h-2.5 mr-1" /> Lesson
                                                </>
                                              )}
                                              {lesson.duration ? ` • ${formatSeconds(lesson.duration)}` : ''}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Message when module has no lessons */}
                                {module.lessons && module.lessons.length === 0 && (
                                  <div className="ml-3.5 pl-5 border-l-2 border-gray-200 py-2.5">
                                    <p className="text-gray-500 text-xs">No lessons available for this module.</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Message when expanded but no modules */}
                        {isExpanded && courseWithLessons && (!courseWithLessons.modules || courseWithLessons.modules.length === 0) && (
                          <div className="ml-3.5 pl-5 border-l-2 border-purple-200 py-2.5">
                            <p className="text-gray-500 text-xs">No modules available for this course.</p>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-500 text-sm">No courses available in this package.</p>
                </div>
              )}
            </div>
            
            {/* FAQs */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Frequently Asked Questions</h3>
              {packageData.faqs.length > 0 ? (
                <div className="space-y-2.5">
                  {packageData.faqs.map((faq: any, index: number) => (
                    <div key={index} className="border border-gray-200 rounded-lg">
                      <button
                        className="flex justify-between items-center w-full text-left p-3 hover:bg-gray-50 transition-colors"
                        onClick={() => toggleFaq(index)}
                      >
                        <span className="font-medium text-gray-900 pr-3 text-sm">{faq.question}</span>
                        <ChevronDown className={`text-gray-500 transition-transform flex-shrink-0 ${
                          activeFaq === index ? 'rotate-180' : ''
                        }`} size={16} />
                      </button>
                      {activeFaq === index && (
                        <div className="px-3 pb-3 border-t border-gray-200">
                          <p className="text-gray-700 leading-relaxed pt-2.5 text-sm">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-500 text-sm">No FAQs available for this package.</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Compact Cart Sidebar - Only show if status is Published */}
          {packageData.status === "Published" && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-gray-200 shadow-lg sticky top-6 overflow-hidden">
                {/* Banner Preview */}
                <div className="p-3.5">
                  <div className="relative aspect-[16/9] rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    {bannerSrc ? (
                      <img
                        src={bannerSrc}
                        alt={packageData.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = "https://via.placeholder.com/400x300/f3f4f6/6b7280?text=Package+Preview"
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <ImageIcon size={24} className="mx-auto mb-1.5 text-gray-400" />
                          <span className="text-xs font-medium">Package Preview</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Pricing Section */}
                <div className="bg-gray-50 px-3.5 py-3.5 border-t border-gray-200">
                  <div className="space-y-3">
                    <div className="text-center">
                      {priceOptions.length > 0 ? (
                        <>
                          {selectedPriceOption && (
                            <div className="text-center">
                              <div className="text-lg font-bold text-gray-900">₹{selectedPriceOption.actualPrice}</div>
                              {selectedPriceOption.price && selectedPriceOption.price !== selectedPriceOption.actualPrice && (
                                <div className="text-xs text-gray-500 line-through">₹{selectedPriceOption.price}</div>
                              )}
                              {selectedPriceOption.period && (
                                <div className="text-xs text-gray-600 mt-1">
                                  Valid for {formatValidityPeriod(selectedPriceOption.period)}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {priceOptions.length > 1 && (
                            <div className="mt-2.5">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Other options:</label>
                              <select
                                value={selectedPriceOptionIndex}
                                onChange={(e) => {
                                  setSelectedPriceOptionIndex(Number(e.target.value));
                                }}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              >
                                {priceOptions.map((option, index) => (
                                  <option key={index} value={index}>
                                    {`₹${option.actualPrice} - ${formatValidityPeriod(option.period)}`}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-lg font-bold text-gray-900">₹{packageData.price}</div>
                      )}
                    </div>
                    <button
                      onClick={handleBuyNow}
                      className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white py-2 px-3 rounded-lg font-semibold text-xs transition-all duration-200 transform hover:-translate-y-0.5"
                    >
                      🛒Buy Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Show status message when package is not Published */}
          {packageData.status !== "Published" && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-gray-200 shadow-lg sticky top-6 overflow-hidden">
                {/* Banner Preview */}
                <div className="p-3.5">
                  <div className="relative aspect-[16/9] rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                    {bannerSrc ? (
                      <img
                        src={bannerSrc}
                        alt={packageData.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = "https://via.placeholder.com/400x300/f3f4f6/6b7280?text=Package+Preview"
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <ImageIcon size={24} className="mx-auto mb-1.5 text-gray-400" />
                          <span className="text-xs font-medium">Package Preview</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Status Message */}
                <div className="bg-gray-50 px-3.5 py-3.5 border-t border-gray-200">
                  <div className="text-center space-y-2.5">
                    <div className={`rounded-lg p-2.5 ${
                      packageData.status === "Draft" ? "bg-blue-50 border border-blue-200" :
                      packageData.status === "Sold out" ? "bg-red-50 border border-red-200" :
                      "bg-gray-50 border border-gray-200"
                    }`}>
                      <div className={`font-semibold text-xs ${
                        packageData.status === "Draft" ? "text-blue-600" :
                        packageData.status === "Sold out" ? "text-red-600" :
                        "text-gray-600"
                      }`}>
                        {packageData.status === "Draft" ? "📝 Draft" :
                         packageData.status === "Sold out" ? "❌ Sold Out" :
                         `Status: ${packageData.status}`}
                      </div>
                      <p className={`text-xs mt-1 ${
                        packageData.status === "Draft" ? "text-blue-500" :
                        packageData.status === "Sold out" ? "text-red-500" :
                        "text-gray-500"
                      }`}>
                        {packageData.status === "Draft" ? "This package is currently in draft mode and not available for purchase." :
                         packageData.status === "Sold out" ? "This package is currently sold out and unavailable for purchase." :
                         `This package is currently in ${packageData.status.toLowerCase()} mode and not available for purchase.`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PackageLandingPage